import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import StarRating from "@/components/StarRating";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || "https://uktranspet.com";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reviewsData, setReviewsData] = useState({ reviews: [], aggregate: { count: 0, average: 0 } });

  const sanitizedHtml = useMemo(
    () => (post ? DOMPurify.sanitize(post.content, { USE_PROFILES: { html: true } }) : ""),
    [post],
  );

  const jsonLd = useMemo(() => {
    if (!post) return [];
    const canonicalUrl = `${SITE_URL}/blog/${post.slug}`;
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: [post.image_url],
        author: { "@type": "Organization", name: "UK Pet Transport" },
        publisher: {
          "@type": "Organization",
          name: "UK Pet Transport",
          logo: { "@type": "ImageObject", url: `${SITE_URL}/brand/apple-touch-icon.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
        inLanguage: "en-GB",
        about: post.route_from && post.route_to ? `Pet transport ${post.route_from} to ${post.route_to}` : post.category,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: canonicalUrl },
        ],
      },
    ];
    // FAQPage schema for Google rich results
    if (Array.isArray(post.faqs) && post.faqs.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    // Service schema per route
    if (post.route_from && post.route_to && post.price_passat) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: `Pet transport ${post.route_from} to ${post.route_to}`,
        provider: { "@type": "Organization", name: "UK Pet Transport" },
        areaServed: [post.route_from, post.route_to],
        offers: {
          "@type": "Offer",
          priceCurrency: "GBP",
          price: String(post.price_passat),
          availability: "https://schema.org/InStock",
          url: `${SITE_URL}/blog/${post.slug}`,
        },
      });
    }
    // AggregateRating + Review schema — ONLY when real approved reviews exist (Google policy)
    const agg = reviewsData?.aggregate;
    if (agg && agg.count > 0 && post.route_from && post.route_to) {
      // Attach AggregateRating to the Service schema (above) via a merged copy
      const svcIdx = schemas.findIndex((s) => s["@type"] === "Service");
      if (svcIdx >= 0) {
        schemas[svcIdx] = {
          ...schemas[svcIdx],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(agg.average),
            reviewCount: agg.count,
            bestRating: "5",
            worstRating: "1",
          },
          review: (reviewsData.reviews || []).slice(0, 10).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.customer_name || "Customer" },
            reviewRating: {
              "@type": "Rating",
              ratingValue: String(r.rating),
              bestRating: "5",
              worstRating: "1",
            },
            reviewBody: r.body,
            datePublished: r.approved_at || r.created_at,
          })),
        };
      }
    }
    return schemas;
  }, [post, reviewsData]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/blogs/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // Fetch approved reviews for this route (silent on failure — zero reviews is a valid state)
    api.get(`/reviews`, { params: { slug } })
      .then(({ data }) => setReviewsData(data))
      .catch(() => setReviewsData({ reviews: [], aggregate: { count: 0, average: 0 } }));
  }, [slug]);

  if (loading) return <div className="p-20 text-center text-muted-foreground">Loading…</div>;
  if (notFound) return (
    <div className="p-20 text-center">
      <SEO title="Article not found" description="This journal article could not be found." path={`/blog/${slug}`} noindex />
      <p className="text-muted-foreground">Article not found.</p>
      <Button asChild className="mt-6"><Link to="/blog">Back to journal</Link></Button>
    </div>
  );

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image_url}
        type="article"
        jsonLd={jsonLd}
      />
      <article className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="blog-post">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 mb-8 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <span className="text-border">/</span>
          <Link to="/blog" className="hover:text-foreground">Journal</Link>
          <span className="text-border">/</span>
          <Link to="/routes" className="hover:text-foreground" data-testid="blog-to-routes-hub">Routes</Link>
          <span className="text-border">/</span>
          <span className="text-foreground truncate" aria-current="page">{post.title.split(" — ")[0]}</span>
        </nav>

        <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-6">
          <ArrowLeft size={14} /> Back to journal
        </Link>
        <span className="block text-xs uppercase tracking-[0.2em] text-accent font-semibold mt-2">{post.category}</span>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3 leading-[1.05]">{post.title}</h1>
        {reviewsData.aggregate.count > 0 && (
          <div className="mt-4" data-testid="blog-stars">
            <StarRating value={reviewsData.aggregate.average} size={20} testId="blog-rating-stars" />
          </div>
        )}
        <p className="text-lg text-muted-foreground mt-6">{post.excerpt}</p>

        <div className="aspect-[16/9] rounded-lg overflow-hidden border border-border mt-10 mb-10">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="prose prose-lg max-w-none [&_h2]:font-display [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:font-display [&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />

        <Card className="mt-12 p-8 border-border bg-secondary/40">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Pricing for this route</p>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div><p className="text-xs text-muted-foreground">Passat</p><p className="font-display text-2xl">£{post.price_passat}</p></div>
            <div><p className="text-xs text-muted-foreground">E-Class</p><p className="font-display text-2xl">£{post.price_eclass}</p></div>
            <div><p className="text-xs text-muted-foreground">V-Class</p><p className="font-display text-2xl">£{post.price_vclass}</p></div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button asChild data-testid="blog-cta-quote"><Link to="/quote">Get a quote</Link></Button>
            <Button asChild data-testid="blog-cta-book-route">
              <Link to={`/book?pickup=${encodeURIComponent(post.route_from || "")}&dropoff=${encodeURIComponent(post.route_to || "")}&miles=${post.distance_miles || ""}`}>
                Book this route
              </Link>
            </Button>
          </div>
        </Card>

        <div className="mt-10 text-center text-sm text-muted-foreground">
          <Link to="/routes" className="hover:text-foreground underline underline-offset-4" data-testid="blog-footer-routes-link">
            Browse all 60 pet transport routes →
          </Link>
        </div>
      </article>
    </>
  );
}
